import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Combobox } from '@/components/ui/combobox';
import { useActivityStore } from '@/store/activityStore';
import { useActivityTypeStore } from '@/store/activityTypeStore';
import { useAuthStore } from '@/store/authStore';
import { apiClient } from '@/utils/api';
import { toast } from 'sonner';
import { DateTimePickerItalian } from '@/components/ui/datetime-picker-italian';

type Priority = 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';
type ActivityStatus = 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';

const formatDateTimeLocal = (date: Date | string): string => {
  const d = typeof date === 'string' ? new Date(date) : date;
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

export default function ActivityEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user: currentUser } = useAuthStore();
  const { activityTypes, fetchActivityTypes } = useActivityTypeStore();
  const [users, setUsers] = useState<Array<{ id: string; firstName: string; lastName: string; email: string }>>([]);
  
  const {
    currentActivity: activity,
    fetchActivity,
    updateActivity,
    loading,
  } = useActivityStore();

  const [formData, setFormData] = useState({
    type: '',
    title: '',
    description: '',
    dueDate: '',
    priority: 'MEDIUM' as Priority,
    status: 'PENDING' as ActivityStatus,
    userId: '',
  });

  useEffect(() => {
    fetchActivityTypes(true);
    if (id) {
      fetchActivity(id).catch(() => {
        toast.error('Errore nel caricamento dell\'attività');
        navigate('/activities');
      });
    }
  }, [id, fetchActivity, navigate, fetchActivityTypes]);

  useEffect(() => {
    // Fetch users (endpoint accessibile a tutti gli utenti autenticati)
    apiClient.get('/users/assignable')
      .then((response) => {
        const usersData = response.data?.data || [];
        setUsers(usersData);
      })
      .catch(() => {
        if (currentUser) {
          setUsers([{
            id: currentUser.id,
            firstName: currentUser.firstName || '',
            lastName: currentUser.lastName || '',
            email: currentUser.email || '',
          }]);
        }
      });
  }, [currentUser]);

  useEffect(() => {
    if (activity) {
      setFormData({
        type: activity.type || '',
        title: activity.title || '',
        description: activity.description || '',
        dueDate: activity.dueDate ? formatDateTimeLocal(activity.dueDate) : '',
        priority: (activity as any).priority || 'MEDIUM',
        status: activity.status || 'PENDING',
        userId: activity.userId || '',
      });
    }
  }, [activity]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!id) return;

    // Validazione data retroattiva
    if (formData.dueDate) {
      const dueDate = new Date(formData.dueDate);
      const now = new Date();
      if (dueDate < now) {
        toast.error('La data di scadenza non può essere nel passato');
        return;
      }
    }

    try {
      const activityData: any = {
        type: formData.type,
        title: formData.title,
        description: formData.description || undefined,
        dueDate: formData.dueDate ? new Date(formData.dueDate).toISOString() : undefined,
        priority: formData.priority,
        status: formData.status,
        userId: formData.userId || undefined,
      };

      await updateActivity(id, activityData);
      toast.success('Attività modificata con successo');
      
      // Se l'attività appartiene a una pratica, torna alla pratica invece che alla pagina attività
      if (activity?.practiceId) {
        navigate(`/practices/${activity.practiceId}`);
      } else {
        navigate(`/activities/${id}`);
      }
    } catch (error: any) {
      console.error('Error updating activity:', error);
      toast.error(error.response?.data?.message || 'Errore nella modifica dell\'attività');
    }
  };

  if (loading && !activity) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/activities')}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Torna alle attività
          </Button>
        </div>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!activity) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/activities')}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Torna alle attività
          </Button>
        </div>
        <Card>
          <CardContent className="p-6">
            <div className="text-center">
              <h3 className="text-lg font-medium text-gray-900">Attività non trovata</h3>
              <p className="mt-1 text-sm text-gray-500">
                L'attività richiesta non esiste o non hai i permessi per visualizzarla.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/activities/${id}`)}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Torna all'attività
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Modifica Attività</h1>
            <p className="mt-1 text-sm text-gray-500">
              Modifica i dati dell'attività: {activity.title}
            </p>
          </div>
        </div>
      </div>

      {/* Form */}
      <Card>
        <CardHeader>
          <CardTitle>Informazioni Attività</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="type">Tipo Attività *</Label>
              <Combobox
                options={activityTypes
                  .filter(t => t.isActive)
                  .sort((a, b) => a.order - b.order)
                  .map((type) => ({
                    value: type.code,
                    label: `${type.icon || ''} ${type.name}`.trim(),
                  }))}
                value={formData.type}
                onValueChange={(value) => setFormData({ ...formData, type: value })}
                placeholder="Seleziona tipo attività"
                searchPlaceholder="Cerca tipo attività..."
                emptyMessage="Nessun tipo attività trovato"
              />
            </div>

            <div>
              <Label htmlFor="title">Titolo *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Es: Chiamata cliente per aggiornamento"
                required
              />
            </div>

            <div>
              <Label htmlFor="description">Descrizione</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Descrizione dettagliata dell'attività..."
                rows={4}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="dueDate">Scadenza</Label>
                <DateTimePickerItalian
                  id="dueDate"
                  value={formData.dueDate}
                  onChange={(value) => setFormData({ ...formData, dueDate: value })}
                  placeholder="GG MM AAAA HH:mm"
                  min={new Date()}
                />
              </div>

              <div>
                <Label htmlFor="priority">Priorità</Label>
                <Select
                  value={formData.priority}
                  onValueChange={(value) => setFormData({ ...formData, priority: value as Priority })}
                >
                  <SelectTrigger id="priority">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="LOW">Bassa</SelectItem>
                    <SelectItem value="MEDIUM">Media</SelectItem>
                    <SelectItem value="HIGH">Alta</SelectItem>
                    <SelectItem value="URGENT">Urgente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="status">Stato</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => setFormData({ ...formData, status: value as ActivityStatus })}
                >
                  <SelectTrigger id="status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PENDING">In Attesa</SelectItem>
                    <SelectItem value="IN_PROGRESS">In Corso</SelectItem>
                    <SelectItem value="COMPLETED">Completata</SelectItem>
                    <SelectItem value="CANCELLED">Annullata</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="userId">Assegnatario</Label>
                <Combobox
                  options={users.map(user => ({
                    value: user.id,
                    label: `${user.firstName} ${user.lastName}`,
                    description: user.email || undefined,
                  }))}
                  value={formData.userId}
                  onValueChange={(value) => setFormData({ ...formData, userId: value })}
                  placeholder="Seleziona assegnatario (opzionale)"
                  searchPlaceholder="Cerca utente..."
                  emptyMessage="Nessun utente trovato"
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(`/activities/${id}`)}
              >
                Annulla
              </Button>
              <Button type="submit" disabled={loading || !formData.type || !formData.title}>
                {loading ? 'Salvataggio...' : 'Salva Modifiche'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

